import styles from './copy-place-dialog.module.scss';
import { Col, Row } from 'reactstrap';
import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { makeCourseRequest, useCourseGetRequestHook } from '../../../../requestUtils';
import { Dialog, DialogActions, DialogContent, DialogTitle, GrayOutlinedButton, LoadingButton, SnackBarContext, TextField } from '@frontend-workspace/react-shared-lib';
import { IRoom } from '../../../../_models/room';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface CopyPlaceDialogProps {
  onClose: () => void,
  open: boolean,
  refreshData: () => void,
  roomId: string
}

export function CopyPlaceDialog(props: CopyPlaceDialogProps) {
  const { data: room }: { data: IRoom } = useCourseGetRequestHook(`room/get/${props.roomId}`, undefined);

  const { t } = useTranslation();
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const [loading, setLoading] = useState<boolean>(false);

  const [name, setName] = useState<string>('');
  const [seats, setSeats] = useState<number>(0);
  const [place, setPlace] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [postCode, setPostCode] = useState<string>();
  const [city, setCity] = useState<string>('');

  const handleNameChange = (event: ChangeEvent<HTMLSelectElement>) => setName(event.target.value);
  const handleSeatsChange = (event: ChangeEvent<HTMLSelectElement>) => setSeats(parseInt(event.target.value));
  const handlePlaceChange = (event: ChangeEvent<HTMLSelectElement>) => setPlace(event.target.value);
  const handleAddressChange = (event: ChangeEvent<HTMLSelectElement>) => setAddress(event.target.value);
  const handlePostCodeChange = (event: ChangeEvent<HTMLSelectElement>) => setPostCode(event.target.value);
  const handleCityChange = (event: ChangeEvent<HTMLSelectElement>) => setCity(event.target.value);

  useEffect(() => {
    if (room) {
      setName(room.name);
      setSeats(room.seats);
      setPlace(room.place + ` (${t('standard.copy')})`);
      setAddress(room.address);
      setPostCode(room.postCode);
      setCity(room.city);
    }
  }, [room]);

  const missingFields = (): boolean => {
    const requiredFieldsFilled =
      name &&
      seats &&
      address &&
      city;
    return requiredFieldsFilled ? false : true;
  };

  const handleAddPlace = () => {
    setLoading(true);
    const body = {
      name: name,
      seats: seats,
      place: place,
      address: address,
      postCode: postCode,
      city: city
    }
    makeCourseRequest(`room/upsert`, 'POST', body).then(result => {
      setSnackbarMessage({ content: t('standard.addedLocationToList'), type: "info", });
      setLoading(false);
      props.refreshData();
      props.onClose();
    }).catch(() => setSnackbarMessage({ content: t('copyPlace.couldNotAddPlace'), type: "error" }));
  };

  return (
    <Dialog
      open={props.open}
      maxWidth="sm"
      fullWidth>
      <DialogTitle className='mb-4'>
        {t('standard.newPlace')} <small className='MuiTypography-body3'>({t('standard.copy')})</small>
      </DialogTitle>
      <DialogContent>
        <div className='mb-3'>
          <TextField
            label={<span>{t('standard.name')} &#42;</span>}
            name='name'
            fullWidth
            sx={{ minWidth: 300 }}
            value={name || ''}
            onChange={handleNameChange}
          />
        </div>
        <Row>
          <Col xl={3}>
            <div className='mb-3'>
              <TextField
                label={<span>{t('standard.seats')} &#42;</span>}
                name='seats'
                type='number'
                sx={{ minWidth: 100 }}
                value={seats || null}
                onChange={handleSeatsChange}
              />
            </div>
          </Col>
        </Row>
        <div className='mb-3'>
          <TextField
            label={t('standard.place')}
            name='place'
            fullWidth
            sx={{ minWidth: 300 }}
            value={place || ''}
            onChange={handlePlaceChange}
          />
        </div>
        <div className='mb-3'>
          <TextField
            label={<span>{t('standard.address')} &#42;</span>}
            name='address'
            fullWidth
            sx={{ minWidth: 300 }}
            value={address || ''}
            onChange={handleAddressChange}
          />
        </div>

        <Row>
          <Col xl={4}>
            <div className='mb-3'>
              <TextField
                label={t('standard.postCode')}
                name='postCode'
                sx={{ minWidth: 100 }}
                value={postCode || ''}
                onChange={handlePostCodeChange}
              />
            </div>
          </Col>
          <Col xl={8}>
            <div className='mb-3'>
              <TextField
                label={<span>{t('standard.city')} &#42;</span>}
                fullWidth
                name='city'
                sx={{ minWidth: 300 }}
                value={city || ''}
                onChange={handleCityChange}
              />
            </div>
          </Col>
        </Row>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          variant="contained"
          size='large'
          onClick={handleAddPlace}
          disabled={loading || missingFields()}
          loading={loading}
          className='minButtonWidth'>
          {loading ? '' : t('copyPlace.createPlace')}
        </LoadingButton>
        <GrayOutlinedButton
          variant="outlined"
          size='large'
          onClick={props.onClose}
          className='minButtonWidth'>
          {t('standard.cancel')}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog>
  );
}

export default CopyPlaceDialog;
